// This file implements custom elements for Community widgets: cc-generic-hero-search and cc-mega-menu.
//
// Global Configuration:
// These widgets can be configured globally by defining a ccWidgetEmbedConfig object:
// <script>
// window.ccWidgetEmbedConfig = {
//   communityUrl: "https://community.example.com",
//   rewriteRelativeLinks: true,
//   rewriteLocalAssets: true
// };
// </script>
//
// Loading Coordination:
// The system uses a two-phase loading approach for Preact compatibility:
// 1. {community_url}/widget/resources (CSS and state) - loaded per widget into shadow DOM
// 2. Widget-specific content (hero/mega-menu) - loaded per widget into temporary light DOM container
// 3. Mutation observer watches temp container for Preact initialization
// 4. {community_url}/widget/resourcesBody (Preact initialization) - loaded once globally into document.head
// 5. When Preact initializes content, mutation observer moves it from temp container to shadow DOM
// 6. Relative link rewriting setup - applied per widget if enabled
//
// This ensures Preact can find and initialize content before it moves to shadow DOM encapsulation.

import { setupFetchRewrite } from '../../helpers/fetchRewrite';
import { observeRelativeLinks } from '../../helpers/rewriteRelativeLinks';
import { loadGenericHeroWidget, loadMegaMenuWidget, loadWidgetResources, loadWidgetResourcesBody } from './widgetResources';
import { insidedIconsCSS } from './insided-icons';

// -----------------------------
// Types & globals
// -----------------------------

declare global {
  interface Window {
    communityUrl?: string;
    CCCustomComponents?: { loaded: boolean; version: string };
    ccWidgetEmbedConfig?: WidgetConfig;
    ccDisableFetchRewrite?: () => void;
    __ccFetchOrig?: typeof fetch;
    __ccXHROrigOpen?: typeof XMLHttpRequest.prototype.open;
    __ccXHROrigSetRequestHeader?: typeof XMLHttpRequest.prototype.setRequestHeader;
    inSidedData?: any;
  }
}

// Fragment fetching & widget loader helpers moved to fragment.ts

// -----------------------------
// Global widget coordination
// -----------------------------

interface WidgetConfig {
  communityUrl?: string;
  rewriteRelativeLinks?: boolean;
  rewriteLocalAssets?: boolean;
}

interface GlobalWidgetState {
  config: WidgetConfig;
  configLoaded: boolean;
  fetchRewriteSetup: boolean;
  elements: Set<HTMLElement>;
  elementsWithContentLoaded: Set<HTMLElement>;
  preactInitialized: boolean;
}

const globalState: GlobalWidgetState = {
  config: {},
  configLoaded: false,
  fetchRewriteSetup: false,
  elements: new Set(),
  elementsWithContentLoaded: new Set(),
  preactInitialized: false,
};

function loadGlobalConfig(): WidgetConfig {
  if (globalState.configLoaded) {
    return globalState.config;
  }

  // Check if a global configuration object is defined
  if (typeof window !== 'undefined' && window.ccWidgetEmbedConfig) {
    try {
      const config = window.ccWidgetEmbedConfig;
      globalState.config = {
        communityUrl: config.communityUrl,
        rewriteRelativeLinks: config.rewriteRelativeLinks ?? false,
        rewriteLocalAssets: config.rewriteLocalAssets ?? false,
      };
    } catch (error) {
      console.error('Error loading ccWidgetEmbedConfig:', error);
    }
  }
  
  // If no configuration was set via global object, fallback to element attributes
  if (!globalState.config.communityUrl) {
    // Fallback: try to find config from any existing custom element
    const elements = [
      ...Array.from(document.querySelectorAll('cc-generic-hero-search')),
      ...Array.from(document.querySelectorAll('cc-mega-menu'))
    ];
    
    for (const element of elements) {
      const el = element as HTMLElement;
      if (el.dataset.communityUrl) {
        globalState.config = {
          communityUrl: el.dataset.communityUrl,
          rewriteRelativeLinks: el.dataset.rewriteSearchResultLinks === 'true' || el.dataset.rewriteRelativeLinks === 'true',
          rewriteLocalAssets: el.dataset.rewriteLocalAssets === 'true',
        };
        break;
      }
    }
  }

  globalState.configLoaded = true;
  return globalState.config;
}

async function registerElement(
  element: HTMLElement,
  communityUrl: string,
  rewriteLocalAssets: boolean,
  rewriteRelativeLinks: boolean
) {
  // Load global config first
  const config = loadGlobalConfig();
  
  // Use global config or fallback to element attributes for backward compatibility
  const finalConfig = {
    communityUrl: config.communityUrl || communityUrl,
    rewriteLocalAssets: config.rewriteLocalAssets ?? rewriteLocalAssets ?? false,
    rewriteRelativeLinks: config.rewriteRelativeLinks ?? rewriteRelativeLinks ?? false,
  };

  if (!finalConfig.communityUrl) {
    console.error('No communityUrl found in global config or element attributes');
    return;
  }

  // Update global config with resolved values
  globalState.config = finalConfig;
  globalState.elements.add(element);
  
  // Inject insided icon font CSS into document head (only done once)
  injectInsidedIconFontCSS();
  
  // Setup global fetch rewrite if needed (this only needs to be done once)
  if (finalConfig.rewriteLocalAssets && !globalState.fetchRewriteSetup) {
    globalState.fetchRewriteSetup = true;
    setupFetchRewrite({ enabled: true, targetUrl: finalConfig.communityUrl! });
  }
  
  // Load widget content immediately for this element
  await loadWidgetContent(element as any);
}

function createPlaceholderDiv(element: HTMLElement): HTMLElement {
  const placeholderDiv = document.createElement('div');
  placeholderDiv.id = 'widget-placeholder';
  placeholderDiv.style.minHeight = '240px';
  placeholderDiv.style.width = '100%';
  
  // Add role="img" for accessibility when background image is present
  const heroImage = element.dataset.heroImage;
  if (heroImage) {
    placeholderDiv.setAttribute('role', 'img');
    placeholderDiv.style.backgroundImage = `url("${heroImage}")`;
    placeholderDiv.style.backgroundSize = 'cover';
    placeholderDiv.style.backgroundPositionX = '0%';
    placeholderDiv.style.backgroundPositionY = '0%';
    placeholderDiv.style.backgroundRepeat = 'no-repeat';
  }
  // Apply background color if specified
  const backgroundColorDisplay = element.dataset.backgroundColorDisplay;
  const backgroundColor = element.dataset.backgroundColor;
  if (backgroundColorDisplay === 'solid_color' && backgroundColor) {
    placeholderDiv.style.backgroundColor = backgroundColor;
  }
  
  return placeholderDiv;
}

async function loadWidgetContent(element: HTMLElement) {
  try {
    // Clear the shadow root before loading anything
    const widgetEl = element as any;
    widgetEl.widgetShadowRoot.innerHTML = '';

    // Create a placeholder div for cc-generic-hero-search to reserve space
    if (element.tagName === 'CC-GENERIC-HERO-SEARCH') {
      const placeholderDiv = createPlaceholderDiv(element);
      widgetEl.widgetShadowRoot.appendChild(placeholderDiv);
    }

    // Create a temporary div outside the shadow DOM to initially hold widget content
    const tempContainer = document.createElement('div');
    tempContainer.id = `temp-widget-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // Store reference to the parent element for correct mapping
    (tempContainer as any).__parentElement = element;
    // Hide temp container to prevent UI flash
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.visibility = 'hidden';
    document.body.appendChild(tempContainer);

    // First, load widget resources into the shadow root for styling
    if (element.tagName === 'CC-GENERIC-HERO-SEARCH' || element.tagName === 'CC-MEGA-MENU') {
      await loadWidgetResources(globalState.config.communityUrl!, {
        targetElement: widgetEl.widgetShadowRoot,
        onSuccess: () => {
          // Widget resources loaded into shadow root
        },
        onError: (err: unknown) => {
          console.error('Error loading widget resources into shadow root:', err);
        },
      });
    }

    // Re-enable mutation observer - watch for content changes within data-preact div
    let preactInitialized = false;
    let initialPreactContent = '';
    
    const mutationObserver = new MutationObserver((mutations) => {
      // Look for the data-preact div
      const preactDiv = tempContainer.querySelector('[data-preact]');
      if (preactDiv && !preactInitialized) {
        const currentContent = preactDiv.innerHTML;
        
        // If this is the first time we see the preact div, store initial content
        if (initialPreactContent === '') {
          initialPreactContent = currentContent;
          return;
        }
        
        // Check if the content has actually changed (Preact has initialized)
        if (currentContent !== initialPreactContent && currentContent.trim() !== '') {
          preactInitialized = true;
          mutationObserver.disconnect();
          
          // Call the internal moveWidgetContent function to handle the move
          moveWidgetContent();
        }
      }
    });

    // Start observing the temp container for changes
    mutationObserver.observe(tempContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });

    // Then load the specific widget content into the temp container initially
    if (element.tagName === 'CC-GENERIC-HERO-SEARCH') {
      const heroEl = element as any;
      const title = heroEl.dataset.title ?? '';
      const heroImage = heroEl.dataset.heroImage ?? '';
      const subtitle = heroEl.dataset.subtitle ?? '';
      const align = heroEl.getAlign();
      const backgroundColor = heroEl.dataset.backgroundColor;
      const backgroundColorDisplay = heroEl.dataset.backgroundColorDisplay;
      const titleColor = heroEl.dataset.titleColor;
      const titleColorDisplay = heroEl.dataset.titleColorDisplay;
      const subtitleColor = heroEl.dataset.subtitleColor;
      const subtitleColorDisplay = heroEl.dataset.subtitleColorDisplay;

      await loadGenericHeroWidget({
        communityUrl: heroEl.communityUrl!,
        targetElement: tempContainer, // Load into temp container first
        title,
        heroImage,
        subtitle,
        align,
        backgroundColor,
        backgroundColorDisplay,
        titleColor,
        titleColorDisplay,
        subtitleColor,
        subtitleColorDisplay,
        onSuccess: () => {
          // Widget content loaded into temp container, observer will watch for Preact changes
          // Mark as loaded since we're not moving content automatically
          markElementContentLoaded(heroEl);
        },
        onError: (err: unknown) => {
          console.error('Error loading widget content:', err);
          mutationObserver.disconnect();
          document.body.removeChild(tempContainer);
          markElementContentLoaded(heroEl);
        },
      });
    } else if (element.tagName === 'CC-MEGA-MENU') {
      const menuEl = element as any;
      await loadMegaMenuWidget({
        communityUrl: menuEl.communityUrl!,
        targetElement: tempContainer, // Load into temp container first
        onSuccess: () => {
          // Widget content loaded into temp container, observer will watch for Preact changes
          // Mark as loaded since we're not moving content automatically
          markElementContentLoaded(menuEl);
        },
        onError: (err: unknown) => {
          console.error('Error loading mega menu widget content:', err);
          mutationObserver.disconnect();
          document.body.removeChild(tempContainer);
          markElementContentLoaded(menuEl);
        },
      });
    }

    // After widget content is loaded, check if we need to load the resources body
    // This should only be done once after all widgets have loaded their content
    if (globalState.elementsWithContentLoaded.size === globalState.elements.size && !globalState.preactInitialized) {
      globalState.preactInitialized = true;
      try {
        await loadWidgetResourcesBody(globalState.config.communityUrl!, {
          targetElement: document.head, // This one goes to head as it's global JS
          onSuccess: () => {
            // Relative link rewriting will be set up after content moves to shadow DOM
          }
        });
      } catch (err) {
        console.error('Error loading widget resources body:', err);
      }
    }
  } catch (err) {
    console.error('Error loading content for element:', err);
    markElementContentLoaded(element);
  }
}

function markElementContentLoaded(element: HTMLElement) {
  globalState.elementsWithContentLoaded.add(element);
}

function injectInsidedIconFontCSS() {
  // Check if already injected
  if (document.getElementById('insided-icon-font-css')) {
    return;
  }

  const styleElement = document.createElement('style');
  styleElement.id = 'insided-icon-font-css';
  styleElement.textContent = insidedIconsCSS;
  document.head.appendChild(styleElement);
}

function moveWidgetContent() {
  // Find all temp containers
  const tempContainers = document.querySelectorAll('[id^="temp-widget-container-"]');
  
  if (tempContainers.length === 0) {
    return;
  }
  
  // Process each temp container with its associated custom element
  tempContainers.forEach((tempContainer) => {
    // Get the parent element that was stored when creating the temp container
    const parentElement = (tempContainer as any).__parentElement;
    
    if (!parentElement) {
      return;
    }
    
    const widgetEl = parentElement as any;
    
    if (widgetEl.widgetShadowRoot) {
      // Remove placeholder div if it exists (for cc-generic-hero-search)
      const placeholder = widgetEl.widgetShadowRoot.querySelector('#widget-placeholder');
      if (placeholder) {
        placeholder.remove();
      }
      
      // Move content from temp container to shadow DOM
      const childrenToMove = Array.from(tempContainer.children);
      
      childrenToMove.forEach(child => {
        widgetEl.widgetShadowRoot.appendChild(child);
      });
      
      // Remove temp container
      tempContainer.remove();
      
      // Setup relative link rewriting for shadow DOM content if needed
      if (globalState.config.rewriteRelativeLinks && globalState.config.communityUrl) {
        try {
          observeRelativeLinks(globalState.config.communityUrl, widgetEl.widgetShadowRoot);
        } catch (error) {
          console.error('Error setting up relative link rewriting:', error);
        }
      }
      
      // Mark element as loaded if not already
      if (!globalState.elementsWithContentLoaded.has(widgetEl)) {
        markElementContentLoaded(widgetEl);
      }
    }
  });
}

// -----------------------------
// <cc-generic-hero-search> element
// Displays a hero section with customizable title, image, subtitle, and styling.
// Content attributes: data-title, data-hero-image, data-subtitle, data-align, color options
// Global settings (communityUrl, rewriting) come from JSON config or fallback to element attributes
// -----------------------------

if (!window.CCCustomComponents) {
  window.CCCustomComponents = { loaded: true, version: '0.0.1' };

  class CCGenericHeroSearch extends HTMLElement {
    private initialized = false;
    private communityUrl: string | null = null;
    private widgetShadowRoot: ShadowRoot;

    constructor() {
      super();
      // Create shadow root
      this.widgetShadowRoot = this.attachShadow({ mode: 'open' });
    }

    static get observedAttributes() {
      return [
        'data-title',
        'data-hero-image',
        'data-subtitle',
        'data-community-url',
        'data-rewrite-search-result-links',
        'data-rewrite-relative-links',
        'data-rewrite-local-assets',
        'data-align',
        // Color overrides
        'data-background-color',
        'data-background-color-display',
        'data-title-color',
        'data-title-color-display',
        'data-subtitle-color',
        'data-subtitle-color-display',
      ];
    }

    async connectedCallback() {
      if (!this.initialized) {
        await this.initializeWidget();
      }
    }

    private validateAndGetCommunityUrl(): string | null {
      // Try element attribute first
      if (this.dataset.communityUrl) {
        return this.dataset.communityUrl;
      }
      
      // Fall back to global config
      const config = loadGlobalConfig();
      if (config.communityUrl) {
        return config.communityUrl;
      }
      
      console.error('cc-generic-hero-search: data-community-url is required either as element attribute or in global config. Component will not render.');
      return null;
    }

    private getRewriteSearchResultLinks() {
      // Default: false unless explicitly 'true'
      // Check both the old attribute name and the new one for backward compatibility
      return this.dataset.rewriteSearchResultLinks === 'true' || this.dataset.rewriteRelativeLinks === 'true';
    }

    private getRewriteLocalAssets() {
      // Default: false unless explicitly 'true'
      return this.dataset.rewriteLocalAssets === 'true';
    }

    private getAlign(): 'left' | 'center' {
      const align = this.dataset.align;
      // Validate and default to 'center'
      if (align === 'left') {
        return align;
      }
      return 'center';
    }

    private async initializeWidget() {
      if (this.initialized) return;
      
      this.communityUrl = this.validateAndGetCommunityUrl();
      if (!this.communityUrl) return;
      
      this.initialized = true;

      // Register with global coordination system
      await registerElement(this, this.communityUrl, this.getRewriteLocalAssets(), this.getRewriteSearchResultLinks());
    }
  }

  if (!customElements.get('cc-generic-hero-search')) {
    customElements.define('cc-generic-hero-search', CCGenericHeroSearch);
  }
}

// -----------------------------
// <cc-mega-menu> element
// Displays a mega menu/header navigation from the community.
// No content attributes needed - displays whatever is returned from /widget/header
// Global settings (communityUrl, rewriting) come from JSON config or fallback to element attributes
// -----------------------------

if (!window.CCCustomComponents) {
  window.CCCustomComponents = { loaded: true, version: '0.0.1' };
}

class CCMegaMenu extends HTMLElement {
  private initialized = false;
  private communityUrl: string | null = null;
  private widgetShadowRoot: ShadowRoot;

  constructor() {
    super();
    // Create shadow root
    this.widgetShadowRoot = this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return [
      'data-community-url',
      'data-rewrite-relative-links',
      'data-rewrite-local-assets',
    ];
  }

  async connectedCallback() {
    if (!this.initialized) {
      // Reserve vertical space to prevent content jumping
      this.style.display = 'block';
      this.style.minHeight = '75px';
      await this.initializeWidget();
    }
  }

  private validateAndGetCommunityUrl(): string | null {
    // Try element attribute first
    if (this.dataset.communityUrl) {
      return this.dataset.communityUrl;
    }
    
    // Fall back to global config
    const config = loadGlobalConfig();
    if (config.communityUrl) {
      return config.communityUrl;
    }
    
    console.error('cc-mega-menu: data-community-url is required either as element attribute or in global config. Component will not render.');
    return null;
  }

  private getRewriteSearchResultLinks() {
    // Default: false unless explicitly 'true'
    return this.dataset.rewriteRelativeLinks === 'true';
  }

  private getRewriteLocalAssets() {
    // Default: false unless explicitly 'true'
    return this.dataset.rewriteLocalAssets === 'true';
  }

  private async initializeWidget() {
    if (this.initialized) return;
    
    this.communityUrl = this.validateAndGetCommunityUrl();
    if (!this.communityUrl) return;
    
    this.initialized = true;

    // Register with global coordination system
    await registerElement(this, this.communityUrl, this.getRewriteLocalAssets(), this.getRewriteSearchResultLinks());
  }
}

if (!customElements.get('cc-mega-menu')) {
  customElements.define('cc-mega-menu', CCMegaMenu);
}

export { }; 